import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon, 
  CurrencyDollarIcon,
  UserIcon,
  UserGroupIcon,
  EnvelopeIcon,
  PhoneIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { API_URL } from '../../config/api';
import QuestionnaireForm from './components/QuestionnaireForm';
import AddonSelector from './components/AddonSelector';

const ServiceBooking = () => {
  const { token } = useParams();

  const [service, setService] = useState(null);

  // Add shake animation CSS
  React.useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = `
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
        20%, 40%, 60%, 80% { transform: translateX(5px); }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Helper function to format rate type for display
  const formatRateType = (rateType) => {
    if (!rateType) return '';
    
    switch (rateType) {
      case 'PER_PERSON':
        return 'per person';
      case 'HOURLY':
        return 'per hour';
      case 'FIXED':
        return 'fixed price';
      case 'PER_ITEM':
        return 'per item';
      default:
        return rateType.toLowerCase().replace('_', ' ');
    }
  };

  // Calculate total booking amount including add-ons and bundle discount
  const calculateTotal = () => {
    if (!service) return 0;

    const breakdown = calculatePricingBreakdown();
    if (!breakdown) return 0;

    return breakdown.finalTotal;
  };

  const calculatePricingBreakdown = () => {
    if (!service || selectedSlots.length === 0) return null;

    const slotCount = selectedSlots.length;
    const participantCount = customerForm.participantCount || 1;

    // Calculate service price per session using tiered rate
    let perSessionPrice = 0;
    if (service.rateType === 'PER_PERSON') {
      perSessionPrice = service.rate * participantCount; // service.rate is already the tiered rate from backend
    } else {
      perSessionPrice = service.rate; // service.rate is already the tiered rate from backend
    }

    // Calculate add-ons per session
    let perSessionAddonsPrice = 0;
    if (selectedAddons && selectedAddons.length > 0) {
      perSessionAddonsPrice = selectedAddons.reduce((total, addon) => {
        const baseRate = parseFloat(addon.rate) || 0;
        const quantity = addon.quantity || 1;

        let addonPrice = baseRate;
        if (addon.rateType === 'PER_PERSON') {
          addonPrice = baseRate * participantCount;
        } else if (addon.rateType === 'PER_ITEM') {
          addonPrice = baseRate * quantity;
        } else {
          addonPrice = baseRate;
        }

        return total + addonPrice;
      }, 0);
    }

    const serviceSubtotal = perSessionPrice * slotCount;
    const addonsSubtotal = perSessionAddonsPrice * slotCount;
    const subtotal = serviceSubtotal + addonsSubtotal;

    // Calculate bundle discount
    let bundleDiscount = null;
    let finalServiceTotal = serviceSubtotal;
    let finalTotal = subtotal;

    // Check if bundle discount applies (check both new field and old discountSettings)
    let discountPercentage = null;

    // Check new bundleDiscount field first
    if (service.bundleDiscount && service.bundleDiscount > 0) {
      discountPercentage = service.bundleDiscount;
    }
    // Fall back to old discountSettings structure
    else if (service.discountSettings?.bundleDiscount?.enabled && service.discountSettings?.bundleDiscount?.value > 0) {
      discountPercentage = service.discountSettings.bundleDiscount.value;
    }

    if (discountPercentage) {
      const availableSlots = getAvailableSlots();

      if (selectedSlots.length === availableSlots.length && availableSlots.length > 1) {
        // Bundle discount applies only to service total, not add-ons
        const discountAmount = (serviceSubtotal * discountPercentage) / 100;
        finalServiceTotal = serviceSubtotal - discountAmount;
        finalTotal = finalServiceTotal + addonsSubtotal;

        bundleDiscount = {
          percentage: discountPercentage,
          amount: Math.round(discountAmount * 100) / 100,
          eligible: true,
          message: `${discountPercentage}% bundle discount applied for selecting all ${availableSlots.length} sessions`
        };
      } else {
        bundleDiscount = {
          percentage: discountPercentage,
          amount: 0,
          eligible: false,
          message: `Select all ${availableSlots.length} sessions to receive ${discountPercentage}% bundle discount`
        };
      }
    }

    return {
      slotCount,
      participantCount,
      perSessionPrice,
      perSessionAddonsPrice,
      totalPerSession: perSessionPrice + perSessionAddonsPrice,
      serviceSubtotal,
      addonsSubtotal,
      subtotal,
      bundleDiscount,
      finalServiceTotal,
      finalTotal: Math.round(finalTotal * 100) / 100,
      selectedAddons: selectedAddons || []
    };
  };

  // Helper function to get available slots
  const getAvailableSlots = () => {
    if (!service || !availability) return [];
    return availability.filter(slot => slot.slotsAvailable > 0);
  };

  // Helper function to detect if user is on mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Generate payment URLs with pre-filled amounts
  const generatePaymentUrl = (platform, handle, amount, note) => {
    if (!handle) return null;
    
    // Remove common prefixes like @ or $
    const cleanHandle = handle.replace(/^[@$]/, '');
    const formattedAmount = amount.toFixed(2);
    const encodedNote = encodeURIComponent(note || `Payment for ${service?.name || 'service'}`);
    
    switch(platform) {
      case 'venmo':
        // Venmo deep link for mobile, web link for desktop
        if (isMobile()) {
          return `venmo://paycharge?txn=pay&recipients=${cleanHandle}&amount=${formattedAmount}&note=${encodedNote}`;
        } else {
          return `https://venmo.com/${cleanHandle}?txn=pay&amount=${formattedAmount}&note=${encodedNote}`;
        }
      
      case 'cashapp':
        // Cash App universal link
        return `https://cash.app/$${cleanHandle}/${formattedAmount}`;
      
      case 'paypal':
        // PayPal.me link with amount
        return `https://paypal.me/${cleanHandle}/${formattedAmount}USD`;
      
      case 'zelle':
        // Zelle doesn't support deep linking with amounts
        return null;

      case 'applepay':
        // Apple Pay doesn't support deep linking with amounts
        return null;

      default:
        return null;
    }
  };

  // Handle payment link click
  const handlePaymentClick = (platform, handle) => {
    const amount = paymentData?.totalDue || calculateTotal();
    const serviceName = service?.name || 'Service';
    const bookingDate = selectedSlots.length > 0 ? selectedSlots[0]?.date : 'TBD';
    const note = `${reservationNumber || 'RESERVATION'} gift`;
    
    const paymentUrl = generatePaymentUrl(platform, handle, amount, note);
    
    if (paymentUrl) {
      // Open in new tab/window
      window.open(paymentUrl, '_blank');
    }
  };
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [reservationNumber, setReservationNumber] = useState(null);
  const [reservationData, setReservationData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);

  // Calculate multi-session status using useMemo to avoid timing issues
  const hasMultipleSessions = useMemo(() => {
    console.log('🧮 useMemo: Calculating multi-session status...');
    console.log('🧮 useMemo: selectedSlots:', selectedSlots);
    console.log('🧮 useMemo: reservationData:', reservationData);

    // FIRST: Check selectedSlots (most reliable during booking flow)
    if (selectedSlots && Array.isArray(selectedSlots) && selectedSlots.length > 1) {
      console.log('✅ useMemo: Multiple sessions detected from selectedSlots:', selectedSlots.length);
      return true;
    }

    // SECOND: Try to parse bookedSlots from reservationData
    if (reservationData?.bookedSlots) {
      try {
        const slots = JSON.parse(reservationData.bookedSlots);
        if (Array.isArray(slots) && slots.length > 1) {
          console.log('✅ useMemo: Multiple sessions detected from reservationData.bookedSlots:', slots.length);
          return true;
        }
      } catch (e) {
        console.error('useMemo: Error parsing bookedSlots:', e);
      }
    }

    // THIRD: Fallback to sessionCount
    if (reservationData?.sessionCount && reservationData.sessionCount > 1) {
      console.log('✅ useMemo: Multiple sessions detected from sessionCount:', reservationData.sessionCount);
      return true;
    }

    console.log('❌ useMemo: No multiple sessions detected');
    return false;
  }, [selectedSlots, reservationData]);

  console.log('🎯 useMemo Result: hasMultipleSessions =', hasMultipleSessions);

  const [customerForm, setCustomerForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    participantCount: 1,
    guestNames: [], // Array of guest names for additional participants
    notes: '',
    questionnaireResponses: {}
  });

  const [selectedAddons, setSelectedAddons] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const fetchServiceData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/public/service/${token}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 404) {
          if (errorData.code === 'LINK_EXPIRED') {
            setError('This booking link has expired. Please contact the organizer for a new link.');
          } else {
            setError('Service not found or link has expired');
          }
        } else if (response.status === 410) {
          if (errorData.code === 'EVENT_EXPIRED') {
            setError('This event has ended and is no longer available for booking.');
          } else {
            setError('This event is no longer available');
          }
        } else {
          setError('Failed to load service information');
        }
        return;
      }

      const result = await response.json();
      const serviceData = result.data || result;

      // Debug logging to see what photo data we're receiving
      console.log('🖼️ [FRONTEND] Received service data:', serviceData.name);
      console.log('🖼️ [FRONTEND] Photo field:', serviceData.photo ?
        (typeof serviceData.photo === 'string' ? serviceData.photo.substring(0, 100) + '...' : JSON.stringify(serviceData.photo)) : 'null');
      console.log('🖼️ [FRONTEND] PhotoUrls field:', serviceData.photoUrls);

      setService(serviceData);

      // Parse availability - check for both enhanced sessions and regular availability
      let parsedAvailability = [];
      try {
        if (serviceData.enableSessions && serviceData.sessions) {
          // Enhanced sessions mode - convert sessions to availability format
          const sessions = Array.isArray(serviceData.sessions) ? serviceData.sessions :
            (typeof serviceData.sessions === 'string' ? JSON.parse(serviceData.sessions) : []);

          parsedAvailability = sessions.map(session => ({
            date: session.date,
            startTime: session.startTime,
            // Calculate end time from start time and duration (hours)
            endTime: session.startTime ? (() => {
              const [hours, minutes] = session.startTime.split(':').map(Number);
              const durationHours = session.duration || 1;
              const totalMinutes = (hours * 60) + minutes + (durationHours * 60);
              const endHours = Math.floor(totalMinutes / 60);
              const endMins = totalMinutes % 60;
              return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
            })() : '',
            slotsAvailable: session.slotsAvailable !== undefined ? session.slotsAvailable : 1,
            originalSlotsAvailable: session.slotsAvailable !== undefined ? session.slotsAvailable : 1,
            sessionName: session.sessionName,
            sessionDescription: session.sessionDescription
          }));
        } else {
          // Regular availability mode
          if (typeof serviceData.availability === 'string') {
            parsedAvailability = JSON.parse(serviceData.availability);
          } else if (Array.isArray(serviceData.availability)) {
            parsedAvailability = serviceData.availability;
          }
        }
      } catch (e) {
        console.error('Error parsing availability:', e);
      }

      console.log('🔍 [DEBUG] Parsed availability before filtering:', parsedAvailability);

      // Filter for available slots only (hide 0 slots entirely)
      const availableSlots = parsedAvailability.filter(slot =>
        slot.slotsAvailable > 0 &&
        new Date(`${slot.date}T${slot.startTime}`) > new Date()
      );

      console.log('🔍 [DEBUG] Available slots after filtering:', availableSlots);
      setAvailability(availableSlots);
    } catch (err) {
      setError('Failed to load service information');
      console.error('Error fetching service:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchServiceData();
  }, [fetchServiceData]);

  const handleSlotSelect = (slot) => {
    const slotIndex = selectedSlots.findIndex(s => s.date === slot.date && s.startTime === slot.startTime);

    if (slotIndex === -1) {
      // Add slot to selection
      setSelectedSlots(prev => [...prev, slot]);
    } else {
      // Remove slot from selection
      setSelectedSlots(prev => prev.filter((_, index) => index !== slotIndex));
    }
  };

  const handleProceedToBooking = () => {
    if (selectedSlots.length === 0) {
      alert('Please select at least one time slot');
      return;
    }

    setShowBookingForm(true);

    // Reset participant count based on the minimum available slots
    const minSlotsAvailable = Math.min(...selectedSlots.map(slot => slot.slotsAvailable !== undefined ? slot.slotsAvailable : 1));
    const maxFromSettings = service.rsvpSettings?.maxParticipants || 10;
    const maxAllowed = Math.min(maxFromSettings, minSlotsAvailable);

    if (customerForm.participantCount > maxAllowed) {
      setCustomerForm(prev => ({
        ...prev,
        participantCount: Math.max(1, maxAllowed)
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!customerForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!customerForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!customerForm.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else {
      // Remove all non-digits to check format
      const phoneDigits = customerForm.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errors.phone = 'Phone number must be at least 10 digits';
      } else if (phoneDigits.length > 11) {
        errors.phone = 'Phone number is too long';
      } else if (phoneDigits.length === 11 && !phoneDigits.startsWith('1')) {
        errors.phone = 'Invalid phone number format';
      }
    }

    if (!customerForm.email.trim()) {
      errors.email = 'Email address is required';
    } else {
      // More comprehensive email validation
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailRegex.test(customerForm.email)) {
        errors.email = 'Please enter a valid email address';
      } else if (customerForm.email.length > 254) {
        errors.email = 'Email address is too long';
      }
    }

    // Validate guest names if collectNames is enabled and participantCount > 1
    if (service?.rsvpSettings?.collectNames && customerForm.participantCount > 1) {
      const guestNamesNeeded = customerForm.participantCount - 1;
      if (!customerForm.guestNames || customerForm.guestNames.length < guestNamesNeeded) {
        errors.guestNames = 'All guest names are required';
      } else {
        customerForm.guestNames.forEach((guest, index) => {
          if (!guest.firstName?.trim()) {
            errors[`guestFirstName_${index}`] = `Guest ${index + 1} first name is required`;
          }
          if (!guest.lastName?.trim()) {
            errors[`guestLastName_${index}`] = `Guest ${index + 1} last name is required`;
          }
        });
      }
    }

    // Validate questionnaire responses
    if (service?.questionnaire?.questions) {
      service.questionnaire.questions.forEach(question => {
        if (question.required) {
          const response = customerForm.questionnaireResponses?.responses?.find(r => r.questionId === question.id);
          if (!response || !response.answer || 
              (Array.isArray(response.answer) && response.answer.length === 0) ||
              (typeof response.answer === 'string' && !response.answer.trim())) {
            errors[question.id] = 'This field is required';
          }
        }
      });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submissions
    if (submitting) {
      return;
    }

    const isValid = validateForm();

    if (!isValid) {

      // Scroll to the first error field
      setTimeout(() => {
        // Find the first input field with an error (red border)
        const firstErrorInput = document.querySelector('input.border-red-300, textarea.border-red-300, select.border-red-300');

        if (firstErrorInput) {
          // Scroll to the error field
          firstErrorInput.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });

          // Focus the input field
          firstErrorInput.focus();

          // Add a subtle shake animation to draw attention
          firstErrorInput.style.animation = 'shake 0.5s ease-in-out';
          setTimeout(() => {
            firstErrorInput.style.animation = '';
          }, 500);
        }
      }, 100);

      return;
    }

    setSubmitting(true);
    
    try {
      // Include user's timezone information for proper datetime handling
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneOffset = new Date().getTimezoneOffset(); // in minutes
      
      const bookingData = {
        selectedSlots: selectedSlots,
        customer: {
          firstName: customerForm.firstName,
          lastName: customerForm.lastName,
          phone: customerForm.phone,
          email: customerForm.email
        },
        participantCount: customerForm.participantCount,
        guestNames: customerForm.guestNames || [],
        userTimezone: userTimezone,
        timezoneOffset: timezoneOffset,
        notes: customerForm.notes,
        allergies: customerForm.allergies || '',
        instagram: customerForm.instagram || '',
        questionnaireResponses: customerForm.questionnaireResponses,
        selectedAddons: selectedAddons,
        isPublicBooking: true,
        isMultiSlot: selectedSlots.length > 1
      };

      console.log('📤 Sending booking data:', JSON.stringify(bookingData, null, 2));

      const response = await fetch(`${API_URL}/public/service/${token}/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) {
        console.error('❌ Booking request failed:', response.status, response.statusText);
        const errorData = await response.json();
        console.error('❌ Server error response:', errorData);
        throw new Error(errorData.message || 'Failed to create booking');
      }

      const result = await response.json();
      setReservationNumber(result.reservation?.reservationNumber);
      setReservationData(result.reservation);
      setPaymentData(result.payment);
      setBookingSuccess(true);
      setShowBookingForm(false);

      // Scroll to top when confirmation page shows
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      // Reset form state to prevent accidental re-submissions
      setCustomerForm({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        participantCount: 1,
        guestNames: [],
        notes: '',
        allergies: '',
        instagram: '',
        questionnaireResponses: null
      });
      setSelectedSlots([]);
      setSelectedAddons([]);
      setFormErrors({});
      
    } catch (err) {
      setError(`Booking failed: ${err.message}`);
      console.error('Booking error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    // Parse date string manually to avoid timezone issues
    // dateStr format: "2025-09-30"
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Simpler date format for confirmation (Month Day, Year)
  const formatDateSimple = (dateStr) => {
    // Parse date string manually to avoid timezone issues
    // dateStr format: "2025-09-30"
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Convert military time to AM/PM format
  const formatTimeAMPM = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading service information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Service Not Available</h1>
          <p className="mt-2 text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 text-center mb-6">
            <CheckCircleIcon className="h-16 w-16 text-yellow-500 mx-auto" />
            <h1 className="mt-4 text-2xl font-bold text-gray-900">Reservation Confirmed - Payment Required</h1>
            <p className="mt-2 text-gray-600">
              Your reservation has been confirmed. Please complete payment below.
            </p>
          </div>

          {/* Payment Section */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Complete Your Payment</h2>
            </div>

            {paymentData?.paymentMethods && Object.keys(paymentData.paymentMethods).some(key => paymentData.paymentMethods[key]) ? (
              <div className="space-y-3">
                {paymentData.paymentMethods.venmo && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-gray-50 space-y-3 sm:space-y-0">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        <span className="text-purple-600 font-bold">V</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">Venmo</div>
                        <div className="text-sm text-gray-500 truncate">{paymentData.paymentMethods.venmo}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePaymentClick('venmo', paymentData.paymentMethods.venmo)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Pay ${(() => {
                        // Calculate correct total
                        const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                        const bundleDiscountAmount = reservationData?.bundleDiscountAmount || 0;
                        const participants = reservationData?.participantCount || 1;
                        const sessionCount = reservationData?.sessionCount || 1;
                        // serviceSubtotal already contains the pre-discount amount
                        const serviceSubtotal = currentServiceSubtotal;
                        const hasAddons = (reservationData?.addons && reservationData.addons.length > 0) ||
                                         (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);
                        const addonsTotal = parseFloat(reservationData?.addonsSubtotal) || 0;
                        const correctTotal = serviceSubtotal + addonsTotal - bundleDiscountAmount;
                        return correctTotal.toFixed(2);
                      })()}
                    </button>
                  </div>
                )}

                {paymentData.paymentMethods.cashApp && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-gray-50 space-y-3 sm:space-y-0">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        <span className="text-green-600 font-bold">$</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">Cash App</div>
                        <div className="text-sm text-gray-500 truncate">{paymentData.paymentMethods.cashApp}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePaymentClick('cashapp', paymentData.paymentMethods.cashApp)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Pay ${(() => {
                        // Calculate correct total
                        const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                        const bundleDiscountAmount = reservationData?.bundleDiscountAmount || 0;
                        const participants = reservationData?.participantCount || 1;
                        const sessionCount = reservationData?.sessionCount || 1;
                        // serviceSubtotal already contains the pre-discount amount
                        const serviceSubtotal = currentServiceSubtotal;
                        const hasAddons = (reservationData?.addons && reservationData.addons.length > 0) ||
                                         (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);
                        const addonsTotal = parseFloat(reservationData?.addonsSubtotal) || 0;
                        const correctTotal = serviceSubtotal + addonsTotal - bundleDiscountAmount;
                        return correctTotal.toFixed(2);
                      })()}
                    </button>
                  </div>
                )}

                {paymentData.paymentMethods.paypal && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-gray-50 space-y-3 sm:space-y-0">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        <span className="text-blue-600 font-bold">P</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">PayPal</div>
                        <div className="text-sm text-gray-500 truncate">{paymentData.paymentMethods.paypal}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePaymentClick('paypal', paymentData.paymentMethods.paypal)}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Pay ${(() => {
                        // Calculate correct total
                        const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                        const bundleDiscountAmount = reservationData?.bundleDiscountAmount || 0;
                        const participants = reservationData?.participantCount || 1;
                        const sessionCount = reservationData?.sessionCount || 1;
                        // serviceSubtotal already contains the pre-discount amount
                        const serviceSubtotal = currentServiceSubtotal;
                        const hasAddons = (reservationData?.addons && reservationData.addons.length > 0) ||
                                         (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);
                        const addonsTotal = parseFloat(reservationData?.addonsSubtotal) || 0;
                        const correctTotal = serviceSubtotal + addonsTotal - bundleDiscountAmount;
                        return correctTotal.toFixed(2);
                      })()}
                    </button>
                  </div>
                )}

                {paymentData.paymentMethods.zelle && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg space-y-3 sm:space-y-0">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        <span className="text-yellow-600 font-bold">Z</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">Zelle</div>
                        <div className="text-sm text-gray-500 truncate">{paymentData.paymentMethods.zelle}</div>
                      </div>
                    </div>
                    <div className="text-center sm:text-right">
                      <div className="text-sm text-gray-500">Amount Due</div>
                      <div className="font-semibold text-lg">${(() => {
                        // Calculate correct total (same as Venmo/PayPal)
                        const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                        const bundleDiscountAmount = reservationData?.bundleDiscountAmount || 0;
                        const participants = reservationData?.participantCount || 1;
                        const sessionCount = reservationData?.sessionCount || 1;
                        // serviceSubtotal already contains the pre-discount amount
                        const serviceSubtotal = currentServiceSubtotal;
                        const hasAddons = (reservationData?.addons && reservationData.addons.length > 0) ||
                                         (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);
                        const addonsTotal = parseFloat(reservationData?.addonsSubtotal) || 0;
                        const correctTotal = serviceSubtotal + addonsTotal - bundleDiscountAmount;
                        return correctTotal.toFixed(2);
                      })()}</div>
                    </div>
                  </div>
                )}

                {paymentData.paymentMethods.applePay && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg space-y-3 sm:space-y-0">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                        <span className="text-gray-800 font-bold">🍎</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">Apple Pay</div>
                        <div className="text-sm text-gray-500 truncate">{paymentData.paymentMethods.applePay}</div>
                      </div>
                    </div>
                    <div className="text-center sm:text-right">
                      <div className="text-sm text-gray-500">Amount Due</div>
                      <div className="font-semibold text-lg">${(() => {
                        // Calculate correct total (same as Venmo/PayPal)
                        const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                        const bundleDiscountAmount = reservationData?.bundleDiscountAmount || 0;
                        const participants = reservationData?.participantCount || 1;
                        const sessionCount = reservationData?.sessionCount || 1;
                        // serviceSubtotal already contains the pre-discount amount
                        const serviceSubtotal = currentServiceSubtotal;
                        const hasAddons = (reservationData?.addons && reservationData.addons.length > 0) ||
                                         (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);
                        const addonsTotal = parseFloat(reservationData?.addonsSubtotal) || 0;
                        const correctTotal = serviceSubtotal + addonsTotal - bundleDiscountAmount;
                        return correctTotal.toFixed(2);
                      })()}</div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Payment instructions will be sent to your email.
              </p>
            )}

            {paymentData?.paymentNote && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-1">Payment Instructions:</p>
                <p className="text-sm text-blue-800">{paymentData.paymentNote}</p>
              </div>
            )}
          </div>

          {/* Reservation Details */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Reservation Details</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Reservation Number</p>
                <p className="font-semibold text-lg text-purple-600">{reservationNumber}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-semibold text-yellow-600">Pending Payment</p>
              </div>
              <div>
                <p className="text-gray-500">Service</p>
                <p className="font-semibold">{service?.name}</p>
              </div>
              {/* Date & Time - show sessions if multiple, otherwise single time */}
              {(() => {
                console.log('🚀 JSX Render: hasMultipleSessions =', hasMultipleSessions);
                return hasMultipleSessions;
              })() ? (
                <div className="col-span-2">
                  <p className="text-gray-500 mb-2">Booked Sessions</p>
                  <div className="space-y-2">
                    {(() => {
                      // Priority order: reservationData.bookedSlots, then selectedSlots, then create mock data
                      let sessionsToDisplay = [];

                      // First try reservation data
                      if (reservationData?.bookedSlots) {
                        try {
                          const parsedSlots = JSON.parse(reservationData.bookedSlots);
                          if (Array.isArray(parsedSlots) && parsedSlots.length > 0) {
                            sessionsToDisplay = parsedSlots;
                            console.log('📋 Using sessions from reservationData.bookedSlots:', parsedSlots);
                          }
                        } catch (e) {
                          console.error('Error parsing reservation bookedSlots:', e);
                        }
                      }

                      // Fallback to selectedSlots if still available
                      if (sessionsToDisplay.length === 0 && selectedSlots && selectedSlots.length > 0) {
                        sessionsToDisplay = selectedSlots;
                        console.log('📋 Using sessions from selectedSlots:', selectedSlots);
                      }

                      // Final fallback: create mock session data if we know there are multiple sessions
                      if (sessionsToDisplay.length === 0 && reservationData?.sessionCount > 1) {
                        console.log('📋 Creating mock session data for', reservationData.sessionCount, 'sessions');
                        // Create basic session entries
                        for (let i = 0; i < reservationData.sessionCount; i++) {
                          sessionsToDisplay.push({
                            sessionName: `Session ${i + 1}`,
                            date: '2025-09-26', // Placeholder - we'll need actual dates
                            startTime: '17:24', // Placeholder
                            duration: 1
                          });
                        }
                      }

                      console.log('📋 Final sessionsToDisplay:', sessionsToDisplay);

                      return sessionsToDisplay.map((slot, index) => (
                      <div key={index} className="bg-blue-50 border border-blue-200 rounded-md p-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-blue-900">
                            Session {index + 1}
                          </span>
                          <span className="text-sm text-blue-600">
                            {slot.sessionName || `${service?.name} - Session ${index + 1}`}
                          </span>
                        </div>
                        <div className="text-sm text-blue-800 mt-1">
                          <span className="font-medium">
                            {(() => {
                              const sessionDate = new Date(`${slot.date}T${slot.startTime}`);
                              return sessionDate.toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              });
                            })()}
                          </span>
                          <span className="ml-2">
                            at {formatTime(slot.startTime)}
                            {slot.endTime && ` - ${formatTime(slot.endTime)}`}
                          </span>
                          {slot.duration && (
                            <span className="ml-2 text-blue-600">
                              ({slot.duration}h)
                            </span>
                          )}
                        </div>
                        {slot.sessionDescription && (
                          <div className="text-xs text-blue-600 mt-1">
                            {slot.sessionDescription}
                          </div>
                        )}
                      </div>
                      ));
                    })()}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-gray-500">Date & Time</p>
                  <p className="font-semibold">
                    {reservationData?.startTime && (() => {
                      const startTime = new Date(reservationData.startTime);
                      return startTime.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) + ` at ${startTime.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}`;
                    })()}
                  </p>
                </div>
              )}
              <div>
                <p className="text-gray-500">Participants</p>
                <p className="font-semibold">{reservationData?.participantCount || 1}</p>
              </div>
              <div>
                <p className="text-gray-500">Total Amount Due</p>
                <p className="font-semibold text-lg text-green-600">
                  ${(() => {
                    // Calculate correct total: Service subtotal + Add-ons subtotal - Bundle discount
                    const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                    const bundleDiscountAmount = reservationData?.bundleDiscountAmount || 0;
                    const participants = reservationData?.participantCount || 1;
                    const sessionCount = reservationData?.sessionCount || 1;

                    // serviceSubtotal already contains the pre-discount amount
                    const serviceSubtotal = currentServiceSubtotal;

                    // Use actual add-ons total from server calculation
                    const hasAddons = (reservationData?.addons && reservationData.addons.length > 0) ||
                                     (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);
                    const addonsTotal = parseFloat(reservationData?.addonsSubtotal) || 0;

                    // Final calculation: service + addons - discount
                    const correctTotal = serviceSubtotal + addonsTotal - bundleDiscountAmount;
                    return correctTotal.toFixed(2);
                  })()}
                </p>
              </div>
            </div>

            {/* Guest Names Section */}
            {(() => {
              // Parse guest names from reservationData which stores them as JSON string
              let guestNames = [];
              try {
                if (reservationData?.guestNames) {
                  guestNames = JSON.parse(reservationData.guestNames);
                }
              } catch (e) {
                // If parsing fails, guest names won't display but won't break the page
                console.warn('Failed to parse guest names:', e);
              }

              if (guestNames && guestNames.length > 0) {
                return (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-500 mb-3">Additional Participants</h3>
                    <div className="space-y-2">
                      {guestNames.map((guest, index) => (
                        <div key={index} className="flex items-center">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {guest.firstName} {guest.lastName}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Financial Information */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h2>

            {/* Financial Breakdown - Responsive Layout */}
            <div className="space-y-3">
              {/* Desktop Table / Mobile Cards */}
              <div className="hidden md:block overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"># Participants</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Individual Sessions - if multiple sessions, show each one */}
                    {(() => {
                      if (hasMultipleSessions && reservationData?.bookedSlots && reservationData.bookedSlots.length > 0) {
                        // Parse booked slots from JSON string if needed
                        let slots = [];
                        try {
                          slots = Array.isArray(reservationData.bookedSlots)
                            ? reservationData.bookedSlots
                            : JSON.parse(reservationData.bookedSlots);
                        } catch (e) {
                          slots = [];
                        }

                        return slots.map((slot, index) => (
                          <tr key={`session-${index}`}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {service?.name} - Session {index + 1}
                              <div className="text-xs text-gray-500">
                                {slot.date} at {slot.startTime}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-center">
                              {reservationData?.participantCount || 1}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-right">
                              ${(service?.rate || 0).toFixed(2)}
                              {service?.rateType === 'PER_PERSON' ? '/person' : ''}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                              ${(() => {
                                const rate = service?.rate || 0;
                                const participants = reservationData?.participantCount || 1;
                                if (service?.rateType === 'PER_PERSON') {
                                  return (rate * participants).toFixed(2);
                                }
                                return rate.toFixed(2);
                              })()}
                            </td>
                          </tr>
                        ));
                      } else {
                        // Single session
                        return (
                          <tr>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {service?.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-center">
                              {reservationData?.participantCount || 1}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 text-right">
                              ${(service?.rate || 0).toFixed(2)}
                              {service?.rateType === 'PER_PERSON' ? '/person' : ''}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                              ${(reservationData?.serviceSubtotal || reservationData?.subtotal || 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      }
                    })()}

                  {/* Add-ons Section - Show detailed breakdown per session if multiple sessions */}
                  {(() => {
                    // Use reservationData.addons if available, otherwise fall back to selectedAddons
                    const addonsToShow = reservationData?.addons || selectedAddons;
                    return addonsToShow && addonsToShow.map((addon, index) => {
                    if (hasMultipleSessions && reservationData?.sessionCount > 1) {
                      // Show per-session breakdown for add-ons
                      const sessionCount = reservationData.sessionCount;
                      const perSessionRate = parseFloat(addon.addonRate || addon.rate || 0);
                      const quantity = addon.quantity || 1;

                      let perSessionAmount = 0;
                      if (addon.rateType === 'PER_PERSON') {
                        perSessionAmount = perSessionRate * (reservationData?.participantCount || 1) * quantity;
                      } else if (addon.rateType === 'PER_ITEM') {
                        perSessionAmount = perSessionRate * quantity;
                      } else {
                        perSessionAmount = perSessionRate * quantity;
                      }

                      return Array.from({ length: sessionCount }, (_, sessionIndex) => (
                        <tr key={`addon-${index}-session-${sessionIndex}`}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {addon.addonName || addon.name} - Session {sessionIndex + 1}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 text-center">
                            {quantity}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 text-right">
                            ${perSessionRate.toFixed(2)}
                            {addon.rateType === 'PER_PERSON' ? '/person' : ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                            ${perSessionAmount.toFixed(2)}
                          </td>
                        </tr>
                      ));
                    } else {
                      // Single session add-on
                      return (
                        <tr key={`addon-${index}`}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {addon.addonName || addon.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 text-center">
                            {addon.quantity || 1}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 text-right">
                            ${parseFloat(addon.addonRate || addon.rate || 0).toFixed(2)}
                            {addon.rateType === 'PER_PERSON' ? '/person' : ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                            ${parseFloat(addon.totalAmount || addon.total || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    }
                    });
                  })()}
                </tbody>
              </table>
              </div>

              {/* Mobile Card Layout */}
              <div className="md:hidden space-y-3">
                {/* Individual Sessions - if multiple sessions, show each one */}
                {(() => {
                  if (hasMultipleSessions && reservationData?.bookedSlots && reservationData.bookedSlots.length > 0) {
                    // Parse booked slots from JSON string if needed
                    let slots = [];
                    try {
                      slots = Array.isArray(reservationData.bookedSlots)
                        ? reservationData.bookedSlots
                        : JSON.parse(reservationData.bookedSlots);
                    } catch (e) {
                      slots = [];
                    }

                    return slots.map((slot, index) => (
                      <div key={`session-mobile-${index}`} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="font-medium text-gray-900 mb-2">
                          {service?.name} - Session {index + 1}
                        </div>
                        <div className="text-xs text-gray-500 mb-3">
                          {slot.date} at {slot.startTime}
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600"># Participants:</span>
                            <span className="font-medium">{reservationData?.participantCount || 1}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Rate:</span>
                            <span className="font-medium">
                              ${(service?.rate || 0).toFixed(2)}
                              {service?.rateType === 'PER_PERSON' ? '/person' : ''}
                            </span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-gray-600">Amount:</span>
                            <span className="font-semibold text-gray-900">
                              ${(() => {
                                const rate = service?.rate || 0;
                                const participants = reservationData?.participantCount || 1;
                                if (service?.rateType === 'PER_PERSON') {
                                  return (rate * participants).toFixed(2);
                                }
                                return rate.toFixed(2);
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ));
                  } else {
                    // Single session
                    return (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="font-medium text-gray-900 mb-3">
                          {service?.name}
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600"># Participants:</span>
                            <span className="font-medium">{reservationData?.participantCount || 1}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Rate:</span>
                            <span className="font-medium">
                              ${(service?.rate || 0).toFixed(2)}
                              {service?.rateType === 'PER_PERSON' ? '/person' : ''}
                            </span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-gray-600">Amount:</span>
                            <span className="font-semibold text-gray-900">
                              ${(reservationData?.serviceSubtotal || reservationData?.subtotal || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                })()}

                {/* Add-ons Mobile Cards */}
                {(() => {
                  const addonsToShow = reservationData?.addons || selectedAddons;
                  if (!addonsToShow || addonsToShow.length === 0) return null;

                  return addonsToShow.map((addon, index) => {
                    if (hasMultipleSessions && reservationData?.sessionCount > 1) {
                      // Show per-session breakdown for add-ons
                      const sessionCount = reservationData.sessionCount;
                      const perSessionRate = parseFloat(addon.addonRate || addon.rate || 0);
                      const quantity = addon.quantity || 1;

                      let perSessionAmount = 0;
                      if (addon.rateType === 'PER_PERSON') {
                        perSessionAmount = perSessionRate * (reservationData?.participantCount || 1) * quantity;
                      } else if (addon.rateType === 'PER_ITEM') {
                        perSessionAmount = perSessionRate * quantity;
                      } else {
                        perSessionAmount = perSessionRate * quantity;
                      }

                      return Array.from({ length: sessionCount }, (_, sessionIndex) => (
                        <div key={`addon-mobile-${index}-session-${sessionIndex}`} className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                          <div className="font-medium text-gray-900 mb-3">
                            {addon.addonName || addon.name} - Session {sessionIndex + 1}
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                {addon.rateType === 'PER_PERSON' ? '# Participants:' : 'Quantity:'}
                              </span>
                              <span className="font-medium">
                                {addon.rateType === 'PER_PERSON' ? (reservationData?.participantCount || 1) : quantity}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Rate:</span>
                              <span className="font-medium">
                                ${perSessionRate.toFixed(2)}
                                {addon.rateType === 'PER_ITEM' ? '/item' : addon.rateType === 'FIXED' ? ' (fixed)' : ''}
                              </span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-gray-600">Amount:</span>
                              <span className="font-semibold text-purple-700">
                                ${perSessionAmount.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ));
                    } else {
                      // Single session add-ons
                      const addonRate = parseFloat(addon.addonRate || addon.rate || 0);
                      const quantity = addon.quantity || 1;
                      const addonAmount = parseFloat(addon.totalAmount ||
                        (addon.rateType === 'PER_PERSON' ? addonRate * (reservationData?.participantCount || 1) :
                         addon.rateType === 'PER_ITEM' ? addonRate * quantity :
                         addonRate));

                      return (
                        <div key={`addon-mobile-${index}`} className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                          <div className="font-medium text-gray-900 mb-3">
                            {addon.addonName || addon.name}
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                {addon.rateType === 'PER_PERSON' ? '# Participants:' : 'Quantity:'}
                              </span>
                              <span className="font-medium">
                                {addon.rateType === 'PER_PERSON' ? (reservationData?.participantCount || 1) : quantity}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Rate:</span>
                              <span className="font-medium">
                                ${addonRate.toFixed(2)}
                                {addon.rateType === 'PER_ITEM' ? '/item' : addon.rateType === 'FIXED' ? ' (fixed)' : ''}
                              </span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-gray-600">Amount:</span>
                              <span className="font-semibold text-purple-700">
                                ${addonAmount.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  });
                })()}
              </div>

              {/* Service Total - Move to top */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex justify-between text-sm mb-4">
                  <span className="font-medium">Service Total ({reservationData?.participantCount || 1} participant{(reservationData?.participantCount || 1) > 1 ? 's' : ''}):</span>
                  <span className="font-medium">${(() => {
                    // serviceSubtotal already contains the pre-discount amount
                    const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                    return currentServiceSubtotal.toFixed(2);
                  })()}</span>
                </div>
              </div>

              {/* Summary Section */}
              <div className="space-y-2">
                {/* Add-ons Section */}
                {(() => {
                  // Check both addons field and addonsSubtotal to see if we should show add-ons
                  const addonsToShow = reservationData?.addons || selectedAddons;
                  const hasAddons = (addonsToShow && addonsToShow.length > 0) ||
                                   (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);

                  if (!hasAddons) return null;

                  const participants = reservationData?.participantCount || 1;
                  const sessionCount = reservationData?.sessionCount || 1;

                  return (
                    <div className="pb-2 border-b border-gray-200">
                      <h4 className="text-sm font-medium text-purple-600 mb-2">Add-ons</h4>
                      {addonsToShow && addonsToShow.map((addon, index) => {
                        const sessionCount = reservationData?.sessionCount || 1;
                        const participants = reservationData?.participantCount || 1;
                        const perSessionRate = parseFloat(addon.rate) || 0;
                        const quantity = addon.quantity || 1;

                        let perSessionAmount = 0;
                        if (addon.rateType === 'PER_PERSON') {
                          perSessionAmount = perSessionRate * participants * quantity;
                        } else if (addon.rateType === 'PER_ITEM') {
                          perSessionAmount = perSessionRate * quantity;
                        } else {
                          perSessionAmount = perSessionRate * quantity;
                        }

                        const totalAmount = perSessionAmount * sessionCount;

                        return (
                          <div key={index} className="flex justify-between text-sm">
                            <span>
                              {addon.name}
                              {addon.rateType === 'PER_ITEM' && quantity > 1 && ` (${quantity}×)`}
                              {sessionCount > 1 && ` × ${sessionCount} session${sessionCount > 1 ? 's' : ''}`}:
                            </span>
                            <span>${totalAmount.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Service subtotal */}
                <div className="flex justify-between text-sm pt-2">
                  <span className="font-medium">Service subtotal:</span>
                  <span className="font-medium">${(() => {
                    // serviceSubtotal already contains the pre-discount amount
                    const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                    return currentServiceSubtotal.toFixed(2);
                  })()}</span>
                </div>

                {/* Bundle Discount */}
                {reservationData?.bundleDiscountAmount && reservationData.bundleDiscountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Bundle discount ({reservationData.bundleDiscountPercentage}%):</span>
                    <span>-${parseFloat(reservationData.bundleDiscountAmount).toFixed(2)}</span>
                  </div>
                )}

                {/* Add-ons subtotal */}
                {(() => {
                  const hasAddons = (reservationData?.addons && reservationData.addons.length > 0) ||
                                   (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);

                  if (!hasAddons) return null;

                  return (
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Add-ons subtotal:</span>
                      <span className="font-medium">${parseFloat(reservationData?.addonsSubtotal || 0).toFixed(2)}</span>
                    </div>
                  );
                })()}

                {/* Final Total */}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                  <span>Total:</span>
                  <span className="text-purple-600">${(() => {
                    // Calculate correct total: Service subtotal + Add-ons subtotal - Bundle discount
                    const currentServiceSubtotal = reservationData?.serviceSubtotal || 0;
                    const bundleDiscountAmount = reservationData?.bundleDiscountAmount || 0;
                    const participants = reservationData?.participantCount || 1;
                    const sessionCount = reservationData?.sessionCount || 1;

                    // serviceSubtotal already contains the pre-discount amount
                    const serviceSubtotal = currentServiceSubtotal;

                    // Use actual add-ons total from server calculation
                    const hasAddons = (reservationData?.addons && reservationData.addons.length > 0) ||
                                     (reservationData?.addonsSubtotal && parseFloat(reservationData.addonsSubtotal) > 0);
                    const addonsTotal = parseFloat(reservationData?.addonsSubtotal) || 0;

                    // Final calculation: service + addons - discount
                    const correctTotal = serviceSubtotal + addonsTotal - bundleDiscountAmount;
                    return correctTotal.toFixed(2);
                  })()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">What Happens Next?</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
              <li>Complete your payment using one of the methods above</li>
              <li>Keep your reservation number ({reservationNumber}) for reference</li>
              <li>Show up at the scheduled time with your reservation number</li>
            </ol>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Questions? Contact us at{' '}
                {service?.communicationEmail ? (
                  <a 
                    href={`mailto:${service.communicationEmail}`}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    {service.communicationEmail}
                  </a>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {(service.photo || service.photoUrls?.original) && (
            <img
              src={service.photo || service.photoUrls?.original}
              alt={service.name}
              className="w-full h-64 object-cover"
              onError={(e) => {
                console.warn('⚠️ Image failed to load for service:', service.name);
                console.log('Failed URL:', e.target.src);
                // Hide the failed image gracefully
                e.target.style.display = 'none';
                // Hide the parent container if it becomes empty
                const parent = e.target.parentElement;
                if (parent && parent.children.length === 1) {
                  parent.style.display = 'none';
                }
              }}
              onLoad={() => {
                console.log('✅ [DEBUG] Image loaded successfully');
              }}
            />
          )}
          
          <div className="p-6">
            <h1 className="text-3xl font-bold text-gray-900">{service.name}</h1>
            
            {service.description && (
              <p className="mt-4 text-gray-600 leading-relaxed">{service.description}</p>
            )}

            {/* Display custom date/time for specific events */}
            {service.availabilityType === 'CUSTOM' && service.customDate && service.customStartTime && (
              <div className="mt-4 flex items-center text-lg font-medium text-gray-900">
                <CalendarIcon className="h-6 w-6 text-purple-600 mr-2" />
                <span>
                  {new Date(service.customDate + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
                <ClockIcon className="h-6 w-6 text-purple-600 ml-4 mr-2" />
                <span>
                  {formatTimeAMPM(service.customStartTime)}
                  {service.customEndTime && ` - ${formatTimeAMPM(service.customEndTime)}`}
                </span>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-lg font-semibold text-gray-900">
                  ${service.rate} {formatRateType(service.rateType)}
                </span>
              </div>
              
              {service.location && (
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="text-gray-600">{service.location}</span>
                </div>
              )}
            </div>

            {service.notes && service.notes.trim() && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-900">Important Information</h3>
                    <p className="mt-1 text-sm text-blue-800">{service.notes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Available Time Slots */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {service.availabilityType === 'CUSTOM' ? 'Event Details' : 'Available Time Slots'}
          </h2>
          
          {availability.length === 0 ? (
            <div className="text-center py-8">
              <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Available Slots</h3>
              <p className="mt-1 text-sm text-gray-500">
                There are currently no available time slots for this service.
              </p>
            </div>
          ) : (
            <div className={service.availabilityType === 'CUSTOM' ? 'max-w-md mx-auto space-y-2' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
              {availability.map((slot, index) => {
                const isSelected = selectedSlots.some(s => s.date === slot.date && s.startTime === slot.startTime);

                return (
                  <button
                    key={index}
                    onClick={() => handleSlotSelect(slot)}
                    className={`p-4 border rounded-lg transition-colors text-left relative ${
                      service.availabilityType === 'CUSTOM' ? 'w-full' : ''
                    } ${
                      isSelected
                        ? 'border-purple-500 bg-purple-100 shadow-md'
                        : 'border-gray-200 hover:border-purple-500 hover:bg-purple-50'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <div className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
{slot.sessionName && (
                    <div className="text-sm font-semibold text-purple-900 mb-2">
                      {slot.sessionName}
                    </div>
                  )}
                  <div className="flex items-center text-sm font-medium text-gray-900">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {formatDate(slot.date)}
                  </div>
                  <div className="flex items-center mt-2 text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                  </div>
                  {slot.sessionDescription && (
                    <div className="mt-2 text-xs text-gray-600 italic">
                      {slot.sessionDescription}
                    </div>
                  )}
                    <div className="mt-2 text-xs text-gray-500">
                      {service.availabilityType === 'CUSTOM'
                        ? 'Click to select this event'
                        : `${slot.slotsAvailable} slot${slot.slotsAvailable !== 1 ? 's' : ''} available`}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Selected Slots Summary and Proceed Button */}
          {selectedSlots.length > 0 && (
            <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">
                Selected Sessions ({selectedSlots.length})
              </h3>
              <div className="space-y-2 mb-4">
                {selectedSlots.map((slot, index) => (
                  <div key={index} className="flex items-center justify-between text-sm bg-white rounded-md p-2 border">
                    <div>
                      <span className="font-medium">{formatDate(slot.date)}</span>
                      <span className="text-gray-600 ml-2">{formatTime(slot.startTime)} - {formatTime(slot.endTime)}</span>
                    </div>
                    <div className="text-purple-600 font-medium">
                      ${service.rate}
                    </div>
                  </div>
                ))}
              </div>

              {/* Detailed Pricing Breakdown */}
              {(() => {
                const breakdown = calculatePricingBreakdown();
                if (!breakdown) return null;

                return (
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Pricing Breakdown</h4>

                    {/* Per Session Breakdown */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <div className="text-xs font-medium text-gray-500 mb-2">Per Session (×{breakdown.slotCount})</div>

                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            {service.name} {service.rateType === 'PER_PERSON' && breakdown.participantCount > 1 ? `(${breakdown.participantCount} participants)` : ''}
                          </span>
                          <span className="font-medium">${breakdown.perSessionPrice.toFixed(2)}</span>
                        </div>

                        {breakdown.selectedAddons.map(addon => {
                          const baseRate = parseFloat(addon.rate) || 0;
                          const quantity = addon.quantity || 1;
                          let addonPrice = baseRate;

                          if (addon.rateType === 'PER_PERSON') {
                            addonPrice = baseRate * breakdown.participantCount;
                          } else if (addon.rateType === 'PER_ITEM') {
                            addonPrice = baseRate * quantity;
                          } else {
                            addonPrice = baseRate;
                          }

                          return (
                            <div key={addon.id || addon.name} className="flex justify-between">
                              <span className="text-gray-600">
                                {addon.name} {addon.quantity > 1 && `(×${addon.quantity})`}
                              </span>
                              <span className="font-medium">${addonPrice.toFixed(2)}</span>
                            </div>
                          );
                        })}

                        <div className="flex justify-between border-t border-gray-300 pt-1 font-medium">
                          <span>Subtotal per session</span>
                          <span>${breakdown.totalPerSession.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Total Summary */}
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        {/* Pricing Breakdown */}
                        <div className="space-y-1">
                          {breakdown.bundleDiscount && (
                            <>
                              <div className="text-gray-600 text-sm">
                                Service subtotal: <span className="font-medium">${breakdown.serviceSubtotal.toFixed(2)}</span>
                              </div>
                              {breakdown.addonsSubtotal > 0 && (
                                <div className="text-gray-600 text-sm">
                                  Add-ons subtotal: <span className="font-medium">${breakdown.addonsSubtotal.toFixed(2)}</span>
                                </div>
                              )}
                              {breakdown.bundleDiscount && breakdown.bundleDiscount.eligible ? (
                                <div className="text-green-600 text-sm">
                                  Bundle discount ({breakdown.bundleDiscount.percentage}%): <span className="font-medium">-${breakdown.bundleDiscount.amount.toFixed(2)}</span>
                                </div>
                              ) : breakdown.bundleDiscount && breakdown.bundleDiscount.message ? (
                                <div className="text-blue-600 text-xs">
                                  {breakdown.bundleDiscount.message}
                                </div>
                              ) : null}
                            </>
                          )}
                          <div className="text-gray-900 font-semibold text-lg border-t pt-1">
                            Total for {breakdown.slotCount} session{breakdown.slotCount > 1 ? 's' : ''}:
                            <span className="text-purple-600 ml-2">${breakdown.finalTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleProceedToBooking}
                        className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                      >
                        Book Selected Sessions
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Booking Form Modal */}
        {showBookingForm && selectedSlots.length > 0 && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowBookingForm(false)}></div>
              
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <form onSubmit={handleBookingSubmit}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Book Your Reservation</h3>
                    
                    {/* Selected Slot Info */}
                    <div className="mb-6 p-4 bg-purple-50 rounded-lg">
                      <h4 className="font-medium text-purple-900">{service.name}</h4>
                      {selectedSlots.map((slot, index) => (
                        <p key={index} className="text-sm text-purple-700 mt-1">
                          {formatDate(slot.date)} at {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                        </p>
                      ))}
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            <UserIcon className="h-4 w-4 inline mr-1" />
                            First Name *
                          </label>
                          <input
                            type="text"
                            value={customerForm.firstName}
                            onChange={(e) => setCustomerForm({...customerForm, firstName: e.target.value})}
                            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                              formErrors.firstName ? 'border-red-300' : 'border-gray-300'
                            }`}
                            required
                          />
                          {formErrors.firstName && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Last Name *
                          </label>
                          <input
                            type="text"
                            value={customerForm.lastName}
                            onChange={(e) => setCustomerForm({...customerForm, lastName: e.target.value})}
                            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                              formErrors.lastName ? 'border-red-300' : 'border-gray-300'
                            }`}
                            required
                          />
                          {formErrors.lastName && (
                            <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          <PhoneIcon className="h-4 w-4 inline mr-1" />
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={customerForm.phone}
                          onChange={(e) => {
                            // Format phone number as user types
                            let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                            if (value.length >= 6) {
                              value = value.replace(/(\d{3})(\d{3})(\d+)/, '($1) $2-$3');
                            } else if (value.length >= 3) {
                              value = value.replace(/(\d{3})(\d+)/, '($1) $2');
                            }
                            setCustomerForm({...customerForm, phone: value});
                          }}
                          placeholder="(555) 123-4567"
                          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                            formErrors.phone ? 'border-red-300' : 'border-gray-300'
                          }`}
                          required
                        />
                        {formErrors.phone && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                          Email Address *
                        </label>
                        <input
                          type="email"
                          value={customerForm.email}
                          onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                          placeholder="example@email.com"
                          className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 ${
                            formErrors.email ? 'border-red-300' : 'border-gray-300'
                          }`}
                          required
                        />
                        {formErrors.email && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                        )}
                      </div>

                      {/* Participant Count Selector - only show for PER_PERSON rate type */}
                      {service.rateType === 'PER_PERSON' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            <UserGroupIcon className="h-4 w-4 inline mr-1" />
                            Number of Participants *
                          </label>
                          <div className="flex items-center space-x-3 mt-1">
                            <select
                              value={customerForm.participantCount}
                              onChange={(e) => {
                                const newParticipantCount = parseInt(e.target.value);
                                const currentGuestNames = customerForm.guestNames || [];
                                let updatedGuestNames = [...currentGuestNames];

                                // Adjust guest names array based on participant count
                                // We need (participantCount - 1) guest names (excluding the main customer)
                                const guestNamesNeeded = Math.max(0, newParticipantCount - 1);

                                if (guestNamesNeeded > updatedGuestNames.length) {
                                  // Add empty guest names
                                  for (let i = updatedGuestNames.length; i < guestNamesNeeded; i++) {
                                    updatedGuestNames.push({ firstName: '', lastName: '' });
                                  }
                                } else if (guestNamesNeeded < updatedGuestNames.length) {
                                  // Remove excess guest names
                                  updatedGuestNames = updatedGuestNames.slice(0, guestNamesNeeded);
                                }

                                setCustomerForm({
                                  ...customerForm,
                                  participantCount: newParticipantCount,
                                  guestNames: updatedGuestNames
                                });
                              }}
                              className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                            >
                              {Array.from({length: (() => {
                                // Get max participants from service settings, default to 10
                                const maxFromSettings = service.rsvpSettings?.maxParticipants || 10;
                                // For multiple slots, use the minimum available slots across all selected slots
                                const minSlotsAvailable = selectedSlots.length > 0
                                  ? Math.min(...selectedSlots.map(slot => slot.slotsAvailable !== undefined ? slot.slotsAvailable : 1))
                                  : 1;
                                const maxAllowed = Math.min(maxFromSettings, minSlotsAvailable, 50);
                                return Math.max(1, maxAllowed); // At least 1 participant
                              })()}, (_, i) => i + 1).map(num => (
                                <option key={num} value={num}>
                                  {num} {num === 1 ? 'participant' : 'participants'}
                                </option>
                              ))}
                            </select>
                            <span className="text-sm text-gray-500">
                              {service.rateType === 'PER_PERSON' && (
                                <span>
                                  Service: ${(service.rate * customerForm.participantCount).toFixed(2)}
                                </span>
                              )}
                              {service.rateType !== 'PER_PERSON' && (
                                <span>
                                  Service: ${service.rate.toFixed(2)}
                                </span>
                              )}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {service.rsvpSettings?.maxParticipants && (
                              <>Maximum {service.rsvpSettings.maxParticipants} participants allowed per booking.</>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Guest Names Collection - only show if service has collectNames enabled and participant count > 1 */}
                      {service.rateType === 'PER_PERSON' &&
                       service.rsvpSettings?.collectNames &&
                       customerForm.participantCount > 1 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            <UserIcon className="h-4 w-4 inline mr-1" />
                            Guest Names *
                          </label>
                          <p className="text-xs text-gray-500 mb-3">
                            Please provide the names of additional participants (you've already provided your name above).
                          </p>
                          <div className="space-y-3">
                            {customerForm.guestNames.map((guest, index) => (
                              <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Guest {index + 1} - First Name *
                                  </label>
                                  <input
                                    type="text"
                                    value={guest.firstName}
                                    onChange={(e) => {
                                      const updatedGuestNames = [...customerForm.guestNames];
                                      updatedGuestNames[index] = {
                                        ...updatedGuestNames[index],
                                        firstName: e.target.value
                                      };
                                      setCustomerForm({
                                        ...customerForm,
                                        guestNames: updatedGuestNames
                                      });
                                    }}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    placeholder="First name"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Guest {index + 1} - Last Name *
                                  </label>
                                  <input
                                    type="text"
                                    value={guest.lastName}
                                    onChange={(e) => {
                                      const updatedGuestNames = [...customerForm.guestNames];
                                      updatedGuestNames[index] = {
                                        ...updatedGuestNames[index],
                                        lastName: e.target.value
                                      };
                                      setCustomerForm({
                                        ...customerForm,
                                        guestNames: updatedGuestNames
                                      });
                                    }}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    placeholder="Last name"
                                    required
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add-ons Section */}
                      {service.enableAddons && (
                        <AddonSelector
                          serviceId={service.id}
                          service={service}
                          serviceToken={token}
                          selectedAddons={selectedAddons}
                          onAddonsChange={setSelectedAddons}
                          participantCount={customerForm.participantCount}
                        />
                      )}


                      {/* Allergies/Dietary Restrictions Field - Only show if service has collectAllergies enabled */}
                      {service.questionnaire?.collectAllergies && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <ExclamationTriangleIcon className="h-4 w-4 inline mr-1 text-orange-500" />
                            Allergies & Dietary Restrictions
                          </label>
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-md mb-2">
                            <p className="text-sm text-orange-800">
                              Please let us know about any allergies, dietary restrictions, or special needs we should be aware of for your service.
                            </p>
                          </div>
                          <textarea
                            value={customerForm.allergies || ''}
                            onChange={(e) => setCustomerForm({...customerForm, allergies: e.target.value})}
                            rows="3"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                            placeholder="Please describe any allergies, dietary restrictions, or special needs..."
                          />
                        </div>
                      )}

                      {/* Instagram Handle (if enabled) */}
                      {service.questionnaire?.collectInstagram && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Instagram Handle
                          </label>
                          <input
                            type="text"
                            value={customerForm.instagram || ''}
                            onChange={(e) => {
                              let value = e.target.value.trim();
                              // Remove any leading @ symbols and spaces
                              value = value.replace(/^@+\s*/, '');
                              // Remove any instagram.com/ prefixes if user pastes a full URL
                              value = value.replace(/^(https?:\/\/)?(www\.)?instagram\.com\//, '');
                              setCustomerForm({...customerForm, instagram: value});
                            }}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                            placeholder="yourhandle (optional, without @)"
                          />
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Custom Questionnaire */}
                  {service.questionnaire?.questions && service.questionnaire.questions.length > 0 && (
                    <div className="mt-6">
                      <QuestionnaireForm
                        questionnaire={service.questionnaire}
                        responses={customerForm.questionnaireResponses}
                        onResponsesChange={(responses) => setCustomerForm(prevForm => ({
                          ...prevForm, 
                          questionnaireResponses: responses
                        }))}
                        errors={formErrors}
                      />
                    </div>
                  )}
                  
                    {/* Total Summary */}
                    <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                      <div className="space-y-2">
                        {(() => {
                          const sessionCount = selectedSlots.length;
                          const participantCount = customerForm.participantCount;

                          // Calculate service price per session using tiered rate
                          const servicePerSession = service.rateType === 'PER_PERSON' ? service.rate * participantCount : service.rate;

                          if (sessionCount > 1) {
                            // Show individual session breakdown for multi-session bookings
                            return (
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-blue-700 pb-1 border-b border-blue-200">
                                  Sessions
                                </div>
                                {selectedSlots.map((slot, index) => (
                                  <div key={index} className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                      Session {index + 1}: {formatDate(slot.date)} at {formatTime(slot.startTime)}
                                    </span>
                                    <span className="font-medium">${servicePerSession.toFixed(2)}</span>
                                  </div>
                                ))}
                                <div className="flex justify-between text-sm pt-1 border-t border-gray-200">
                                  <span className="text-gray-700 font-medium">
                                    Service Total ({participantCount} {participantCount === 1 ? 'participant' : 'participants'}):
                                  </span>
                                  <span className="font-medium">${(servicePerSession * sessionCount).toFixed(2)}</span>
                                </div>
                              </div>
                            );
                          } else {
                            // Show simple breakdown for single session
                            return (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">
                                  Service ({participantCount} {participantCount === 1 ? 'participant' : 'participants'}):
                                </span>
                                <span className="font-medium">${servicePerSession.toFixed(2)}</span>
                              </div>
                            );
                          }
                        })()}

                        {selectedAddons.length > 0 && (
                          <div className="space-y-2 mt-3">
                            <div className="text-sm font-medium text-purple-700 pb-1 border-b border-purple-200">
                              Add-ons
                            </div>
                            {selectedAddons.map((addon, index) => {
                              const baseRate = parseFloat(addon.rate) || 0;
                              const quantity = addon.quantity || 1;
                              const sessionCount = selectedSlots.length;
                              const participantCount = customerForm.participantCount;

                              // Calculate per-session addon price
                              let addonPerSession = baseRate;
                              if (addon.rateType === 'PER_PERSON') {
                                addonPerSession = baseRate * participantCount;
                              } else if (addon.rateType === 'PER_ITEM') {
                                addonPerSession = baseRate * quantity;
                              }

                              // Total for all sessions
                              const addonTotalAllSessions = addonPerSession * sessionCount;

                              return (
                                <div key={index} className="flex justify-between text-sm">
                                  <span className="text-gray-600">
                                    {addon.name}
                                    {addon.rateType === 'PER_ITEM' && quantity > 1 && ` (${quantity}×)`}
                                    {sessionCount > 1 && ` × ${sessionCount} sessions`}:
                                  </span>
                                  <span className="font-medium">${addonTotalAllSessions.toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        <div className="border-t-2 border-gray-400 pt-3 mt-3">
                          {(() => {
                            const breakdown = calculatePricingBreakdown();
                            return breakdown && breakdown.bundleDiscount ? (
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm text-gray-600">
                                  <span>Service subtotal:</span>
                                  <span>${breakdown.serviceSubtotal.toFixed(2)}</span>
                                </div>
                                {breakdown.bundleDiscount && breakdown.bundleDiscount.eligible && (
                                  <div className="flex justify-between text-sm text-green-600">
                                    <span>Bundle discount ({breakdown.bundleDiscount.percentage}%):</span>
                                    <span>-${breakdown.bundleDiscount.amount.toFixed(2)}</span>
                                  </div>
                                )}
                                {breakdown.addonsSubtotal > 0 && (
                                  <div className="flex justify-between text-sm text-gray-600">
                                    <span>Add-ons subtotal:</span>
                                    <span>${breakdown.addonsSubtotal.toFixed(2)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between text-lg font-semibold border-t pt-2">
                                  <span className="text-gray-900">Total:</span>
                                  <span className="text-purple-600">${breakdown.finalTotal.toFixed(2)}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="flex justify-between text-lg font-semibold">
                                <span className="text-gray-900">Total:</span>
                                <span className="text-purple-600">${calculateTotal().toFixed(2)}</span>
                              </div>
                            );
                          })()}
                        </div>
                        
                        <div className="mt-2 p-2 bg-blue-50 rounded text-center">
                          <p className="text-xs text-blue-600">
                            💳 Payment will be processed on the next page
                          </p>
                        </div>
                      </div>
                    </div>
                  
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ pointerEvents: submitting ? 'none' : 'auto' }}
                    >
                      {submitting ? 'Booking...' : 'Book Reservation'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBookingForm(false)}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceBooking;